import type { ListCopy } from 'lib/listCopy'
import { usPhoneNumber } from 'lib/phone'
import { conditionComplete } from './conditions'
import { type ObjectAvailability, availablePhoneFields } from './dataContext'
import type { TriggerKind } from './triggerCatalog'
import { CARRIER_KEYWORDS } from './replyKeywords'
import { PAPERWORK_LINK_PATTERN } from './paperworkStep'
import type {
  AddNoteNodeData,
  AiAgentSmsNodeData,
  AddToPipelineNodeData,
  AssignOwnerNodeData,
  BranchNodeData,
  CreateTaskNodeData,
  EditObjectNodeData,
  EditTaskNodeData,
  FilterNodeData,
  InitiateCallNodeData,
  LogFollowUpNodeData,
  SendNotificationNodeData,
  SendPaperworkNodeData,
  SendSmsNodeData,
  TagObjectNodeData,
  TargetObject,
  WaitForKeywordNodeData,
  WaitNodeData,
  WaitUntilNodeData,
  WorkflowNodeData,
  WorkflowNodeKind,
} from 'app/types/workflows'

/** Field-keyed validation messages for a node. Empty object means the node is complete.
 *  The `context` key is a node-level error not tied to a specific field (e.g. the trigger can't
 *  satisfy what this action needs); the inspector surfaces it as a banner. */
export type NodeErrors = Record<string, string>

/** Just the part of a Location the business-hours hold depends on. */
export type LocationBusinessHours = {
  timezone?: string | null
  openHours?: Record<string, { open?: boolean | null; hours?: { from: string; to: string }[] | null } | null> | null
}

/** Whether a Wait's "business hours only" hold can actually do anything at this location. Mirrors
 *  the engine's own precondition (workflowSteps/wait.ts): it needs a time zone plus at least one
 *  day that is open AND has an hours range, or `adjustToBusinessHours` is never reached and the
 *  wait resumes at its raw time. Undefined location means unknown, which is not a yes. */
export function locationHasBusinessHours(location?: LocationBusinessHours): boolean {
  if (!location?.timezone || !location.openHours) return false
  return Object.values(location.openHours).some((day) => Boolean(day?.open) && Boolean(day?.hours?.length))
}

/** The trigger-derived context a node is validated against. Optional fields are only checked
 *  when supplied, so node cards can do structural validation without the trigger context. */
export type ValidationContext = {
  /** Fields the trigger provides — drives variable/binding scope (e.g. SMS needs a phone). */
  availableFields?: string[]
  /** Whether the contact object is in scope. */
  contact?: ObjectAvailability
  /** Whether the triggering activity object is in scope. */
  activity?: ObjectAvailability
  /** Whether the workflow's location has an SMS agent that can answer (`smsAgentReady`). Only an
   *  explicit false is an error: unknown (loading, failed, no location) must not block. */
  smsAgentReady?: boolean
  /** The workflow's trigger, for steps that read the triggering record's own fields. */
  triggerKind?: TriggerKind
}

/** What a node needs from the run to function — the answer to "does this action need a contact?".
 *  `contact`/`activity` = acts on that object; `phone` = needs a number to text (contact OR activity);
 *  `none` = runs without either (e.g. notify staff). Structural nodes (trigger/wait/branch/exit)
 *  return `undefined`. For the "object" actions (tag/edit) the answer depends on the chosen target. */
export type ActionNeed = TargetObject | `phone` | `none`

export function actionNeed(kind: WorkflowNodeKind, data?: WorkflowNodeData): ActionNeed | undefined {
  switch (kind) {
    case `aiAgentSms`:
      return `phone`
    case `sendSms`:
      // A custom-number SMS doesn't need a contact phone in scope; a contact-field one does.
      return (data as SendSmsNodeData | undefined)?.toMode === `custom` ? `none` : `phone`
    case `sendNotification`:
      return `none`
    case `createTask`:
    case `addToPipeline`:
    case `initiateCall`:
    case `assignOwner`:
    case `addNote`:
    case `logFollowUp`:
    case `sendPaperwork`:
      return `contact`
    case `tagObject`:
      return (data as TagObjectNodeData | undefined)?.target ?? `contact`
    case `editObject`:
      return (data as EditObjectNodeData | undefined)?.target ?? `contact`
    default:
      return undefined
  }
}

/** Availability of whichever object an action acts on, from the validation context. */
function targetAvailability(target: TargetObject, ctx?: ValidationContext): ObjectAvailability | undefined {
  return target === `activity` ? ctx?.activity : ctx?.contact
}

/** Verb used in the "can't run" message for each object-acting action. */
const actionVerbs = (copy: ListCopy): Partial<Record<WorkflowNodeKind, string>> => ({
  createTask: `create a task`,
  tagObject: `tag`,
  editObject: `edit`,
  addToPipeline: copy.addContactToListIssue,
  initiateCall: `place a call`,
  assignOwner: `assign an owner`,
  addNote: `add a note`,
  logFollowUp: `log a follow-up`,
  sendPaperwork: `send paperwork to`,
})

/** Validates a node's data. Pure — drives both the inspector errors and the node badge.
 *  Returns BLOCKING errors only (these gate Activate); non-blocking advisories live in
 *  `nodeDataWarnings`. `ctx` is the trigger-derived context; context-dependent checks run only
 *  when the relevant context is supplied. */
export function nodeDataErrors(
  kind: WorkflowNodeKind,
  data: WorkflowNodeData,
  copy: ListCopy,
  ctx?: ValidationContext,
): NodeErrors {
  const errors: NodeErrors = {}

  // Hard block: an object-acting step under a trigger that NEVER provides that object can't run.
  const need = actionNeed(kind, data)
  if ((need === `contact` || need === `activity`) && targetAvailability(need, ctx) === `none`) {
    errors.context = `This trigger never provides ${need === `activity` ? `an activity` : `a contact`}, so this step can't ${actionVerbs(copy)[kind]} ${need === `activity` ? `the activity` : `the contact`}.`
  }

  switch (kind) {
    case `waitForKeyword`: {
      const d = data as WaitForKeywordNodeData
      if (!d.options.length) errors.options = `Add at least one reply option.`
      // Each word's first option. A reply can take only one exit, so a later option can't reuse it.
      const ownerOf = new Map<string, string>()
      d.options.forEach((option, index) => {
        if (!option.label.trim()) errors[`options.${index}.label`] = `Name this option.`
        const key = `options.${index}.keywords`
        const carrier = option.keywords.find((word) => CARRIER_KEYWORDS.includes(word))
        const taken = option.keywords.find((word) => ownerOf.has(word))
        if (!option.keywords.length) errors[key] = `Add at least one word, for example C.`
        else if (carrier) {
          errors[key] =
            `${carrier} is a word the carrier acts on (opting the patient out or in) before this workflow sees the reply. Choose another word.`
        } else if (taken) {
          errors[key] =
            `${taken} is already used by ${ownerOf.get(taken) || `another option`}. A reply can only take one exit.`
        }
        for (const word of option.keywords) if (!ownerOf.has(word)) ownerOf.set(word, option.label.trim())
      })
      break
    }
    case `sendSms`: {
      const d = data as SendSmsNodeData
      if (!d.message.trim()) errors.message = `Add a message to send.`
      if (d.toMode === `custom`) {
        if (!d.toNumber?.trim()) errors.toNumber = `Enter a number to text.`
        else if (!usPhoneNumber(d.toNumber)) errors.toNumber = `Enter a full 10-digit phone number.`
      } else if (ctx?.availableFields) {
        const phones = availablePhoneFields(ctx.availableFields)
        if (phones.length === 0) {
          errors.phone = `This workflow's trigger doesn't provide a phone number, so this SMS can't be sent.`
        } else if (!d.toField || !phones.some((field) => field.key === d.toField)) {
          errors.toField = `Choose which number to text.`
        }
      }
      break
    }
    case `aiAgentSms`: {
      const d = data as AiAgentSmsNodeData
      // The brief is required: with no goal the agent only answers whatever the patient raises.
      if (!d.message.trim()) errors.message = `Write the opening message.`
      if (!d.brief.trim()) errors.brief = `Tell the agent why it is reaching out and what a win looks like.`
      if (ctx?.availableFields) {
        const phones = availablePhoneFields(ctx.availableFields)
        if (phones.length === 0) {
          errors.phone = `This workflow's trigger doesn't provide a phone number, so the agent has nowhere to start the conversation.`
        } else if (!d.toField || !phones.some((field) => field.key === d.toField)) {
          errors.toField = `Choose which number to open the conversation on.`
        }
      }
      // The engine skips this step for every contact when the agent isn't ready (no_agent_prompt).
      if (ctx?.smsAgentReady === false) {
        errors.agentNotReady = `This location's SMS agent isn't set up, so this step would skip every contact and send nothing. Ask Trackable to set up the SMS agent for this location.`
      }
      break
    }
    case `initiateCall`: {
      const d = data as InitiateCallNodeData
      // ringNumber is required — the engine skips the step if it's blank.
      if (!d.ringNumber?.trim()) errors.ringNumber = `Enter a number to ring.`
      else if (!usPhoneNumber(d.ringNumber)) errors.ringNumber = `Enter a full 10-digit phone number.`
      break
    }
    case `createTask`: {
      const d = data as CreateTaskNodeData
      if (!d.title.trim()) errors.title = `Give the task a title.`
      // 0 is a real answer — "due the day it runs" — and the speed-to-lead template depends on it.
      // Treating 0 as "unset" is the same falsy-zero mistake that broke the daily cap (TRK-5512).
      if (d.dueType === `relative` && !(d.dueInDays != null && d.dueInDays >= 0))
        errors.dueInDays = `Set how many days (0 = due the same day).`
      if (d.dueType === `absolute` && !d.dueDate) errors.dueDate = `Pick a date.`
      break
    }
    case `tagObject`: {
      const d = data as TagObjectNodeData
      if (d.tags.length === 0) errors.tags = `Select at least one tag.`
      break
    }
    case `addToPipeline`: {
      const d = data as AddToPipelineNodeData
      if (!d.pipelineId) errors.pipelineId = copy.chooseAList
      // Moving requires a target stage; adding can fall back to the pipeline's first stage.
      else if (d.operation === `move` && !d.stage) errors.stage = `Choose a stage to move to.`
      break
    }
    case `editObject`: {
      const d = data as EditObjectNodeData
      if (!d.fields.length) errors.fields = `Add at least one field to update.`
      const seen = new Set<string>()
      d.fields.forEach((row, index) => {
        if (!row.field) errors[`fields.${index}.field`] = `Choose a field.`
        // The engine stores the rows as an object keyed by field name, so a second row on the same
        // field would silently swallow the first. Caught here rather than losing it on save.
        else if (seen.has(row.field)) errors[`fields.${index}.field`] = `This field is already set above.`
        else seen.add(row.field)
        if (row.operation !== `clear` && !row.value.trim()) errors[`fields.${index}.value`] = `Enter a value.`
      })
      break
    }
    case `sendNotification`: {
      const d = data as SendNotificationNodeData
      if (d.recipients.length === 0) errors.recipients = `Choose at least one recipient.`
      if (!d.message.trim()) errors.message = `Add a message.`
      break
    }
    case `assignOwner`: {
      const d = data as AssignOwnerNodeData
      if (!d.ownerId) errors.ownerId = `Choose who to assign as the owner.`
      break
    }
    case `addNote`: {
      const d = data as AddNoteNodeData
      if (!d.body.trim()) errors.body = `Write the note.`
      break
    }
    case `logFollowUp`: {
      const d = data as LogFollowUpNodeData
      const afterCommLog = !ctx?.triggerKind || ctx.triggerKind === `comm_log_created`
      if (!afterCommLog && d.connected === `fromDirection`)
        errors.connected = `"From direction" only works after a PMS comm log trigger.`
      if (!afterCommLog && d.dateSource === `event`)
        errors.dateSource = `"When it happened" only works after a PMS comm log trigger.`
      break
    }
    case `sendPaperwork`: {
      const d = data as SendPaperworkNodeData
      if (!d.paperworkTemplateId) errors.paperworkTemplateId = `Choose a packet to send.`
      if (!(d.expiresInDays >= 1)) errors.expiresInDays = `The link must last at least 1 day.`
      if (!d.message.trim()) errors.message = `Add the text that carries the link.`
      else if (!PAPERWORK_LINK_PATTERN.test(d.message)) errors.message = `Add the paperwork link to the text.`
      if (ctx?.availableFields) {
        const phones = availablePhoneFields(ctx.availableFields)
        if (phones.length === 0)
          errors.phone = `This workflow's trigger doesn't provide a phone number to text the link to.`
        else if (!d.toField || !phones.some((field) => field.key === d.toField))
          errors.toField = `Choose which number to text.`
      }
      break
    }
    case `wait`: {
      const d = data as WaitNodeData
      if (!(d.duration > 0)) errors.duration = `Duration must be at least 1.`
      break
    }
    case `waitUntil`: {
      const d = data as WaitUntilNodeData
      if (!(d.duration > 0)) errors.duration = `Duration must be at least 1.`
      if (d.conditions.length === 0) errors.conditions = `Add at least one condition.`
      else if (!d.conditions.every(conditionComplete)) errors.conditions = `Complete every condition.`
      break
    }
    case `editTask`: {
      const d = data as EditTaskNodeData
      // An empty ownerId is a change (it clears the assignee); empty text is not.
      const changes = [
        d.status,
        d.archived,
        d.ownerId,
        d.dueType,
        d.title?.trim() || undefined,
        d.description?.trim() || undefined,
      ]
      if (changes.every((change) => change === undefined)) {
        errors.changes = `Choose at least one change to make to the task.`
      }
      if (d.dueType === `relative` && !(d.dueInDays != null && d.dueInDays >= 0))
        errors.dueInDays = `Set how many days (0 = due the same day).`
      if (d.dueType === `absolute` && !d.dueDate) errors.dueDate = `Pick a date.`
      break
    }
    case `filter`:
    case `branch`: {
      // Filter and Branch share the condition-group model.
      const d = data as FilterNodeData | BranchNodeData
      if (d.conditions.length === 0) {
        errors.conditions = `Add at least one condition.`
      } else if (!d.conditions.every(conditionComplete)) {
        errors.conditions = `Complete every condition.`
      }
      break
    }
    // trigger, exit have no required configuration.
  }

  return errors
}

/** Non-blocking advisories — surfaced to the user but they do NOT gate Activate. Today: a
 *  contact-targeting action under an activity trigger that only *sometimes* resolves to a contact. */
export function nodeDataWarnings(kind: WorkflowNodeKind, data: WorkflowNodeData, ctx?: ValidationContext): NodeErrors {
  const warnings: NodeErrors = {}
  // A `conditional` object is only ever the contact (the activity is guaranteed when in scope).
  const need = actionNeed(kind, data)
  if (need === `contact` && ctx?.contact === `conditional`) {
    warnings.context =
      `This trigger doesn't always have a contact (e.g. a call from an unknown number). This step ` +
      `is skipped when there's none. Turn on "Require a matched contact" on the ` +
      `trigger to require one.`
  }
  return warnings
}
// nodeErrors / isNodeComplete / workflowIssueCount were folded into `nodeIssues.ts`
// (`computeNodeIssues` + `totalIssueCount`) so the card, inspector, and toolbar share one source.
